import { Component, Input, EventEmitter, Output, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, HostBinding } from '@angular/core';
import { SettingsService } from 'services/settings.service';
import { VirtualKeyboardService } from 'services/virtual-keyboard.service';
import { InputValue } from 'components/virtual-keyboard/virtual-keyboard';
import { MeasureUnitConfig } from '@cf-shared/measure-configs';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { untranslatedString } from 'models/translated-string';
import { LL } from 'i18n/i18n.service';
import { TimeFormat, to12hHour, to24hHour, convertHourForFormat, parseHourText } from './cs-input-time-format';

export { TimeFormat };

@Component({
  selector: 'cs-input-time',
  templateUrl: 'cs-input-time.html',
  host: { 'class': 'input-time' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsInputTime implements OnDestroy {
  @Input() time: string;
  @Input() disabled: boolean = false;
  @Output() timeChange: EventEmitter<string> = new EventEmitter<string>();

  @HostBinding('attr.disabled') get disabledAttr() { return this.disabled ? true : null; }

  @ViewChild('hourInput') hourInput: ElementRef;
  @ViewChild('minuteInput') minuteInput: ElementRef;

  protected hour: string = '';
  protected minute: string = '';
  protected hourEdit: string;
  protected minuteEdit: string;

  protected is24h = true;
  protected isPM = false;
  protected currentFormat: TimeFormat;

  private currentInputControl: HTMLInputElement = undefined;
  // The box the on-screen keyboard was started on. Kept separately from
  // currentInputControl because the keyboard calls this component's onClose —
  // which clears currentInputControl — in two flows where it then stays open
  // and carries on editing the same box: switching between its number pad and
  // its wheels, and moving from the hour box to the minute box. See
  // getKeyboardOptions, which restores currentInputControl from this (#4904).
  private keyboardTargetInput: HTMLInputElement = undefined;
  private isSelectingFormatFromKeyboard = false;

  constructor(
    private settings: SettingsService,
    private popupMenu: PopupMenuProvider,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private keyboardService: VirtualKeyboardService
  ) { }

  ngOnDestroy(): void {
    // Stop keyboard if component is destroyed
    if (this.currentInputControl) {
      void this.keyboardService.stopKeyboard();
      this.currentInputControl = undefined;
    }
  }

  ngOnInit() {
    this.is24h = this.settings.isClock24hr();
    this.updateFormatFromTime();
  }

  ngOnChanges() {
    this.updateFormatFromTime();
  }

  private updateFormatFromTime() {
    if (this.time == undefined) {
      this.isPM = false;
      this.hour = '';
      this.minute = '';
      this.currentFormat = this.is24h ? TimeFormat.TwentyFourHour : TimeFormat.TwelveHourAM;
    } else {
      // Parse HH:mm format time string
      const timeParts = this.time.split(':');
      if (timeParts.length !== 2) {
        this.hour = '';
        this.minute = '';
        return;
      }

      const hourNum = parseInt(timeParts[0]);
      const minuteNum = parseInt(timeParts[1]);

      if (Number.isNaN(hourNum) || Number.isNaN(minuteNum)) {
        this.hour = '';
        this.minute = '';
        return;
      }

      const twelveHour = to12hHour(hourNum);
      this.isPM = twelveHour.isPM;

      // Set hour based on format
      this.hour = this.is24h ? hourNum.toString().padStart(2, '0') : twelveHour.hourText;

      this.minute = minuteNum.toString().padStart(2, '0');

      // Set current format
      if (this.is24h) {
        this.currentFormat = TimeFormat.TwentyFourHour;
      } else {
        this.currentFormat = this.isPM ? TimeFormat.TwelveHourPM : TimeFormat.TwelveHourAM;
      }
    }
  }

  // bring a typed hour into range. The input is digits only, at most two of
  // them. A single digit is left alone: it is a partial entry on its way to
  // two, so "1" must stay "1" long enough for the user to type "18".
  // Shared by the two ways an hour can be typed — on a real keyboard, and on
  // the app's own number pad — so the two cannot drift apart (#4904).
  private clampHourText(digits: string): string {
    if (digits.length !== 2) {
      return digits;
    }
    const hourNum = parseInt(digits);
    if (this.is24h) {
      // if past the last hour of the day, hold at it
      return hourNum > 23 ? '23' : digits;
    }
    // in AM/PM mode the dial runs 1 to 12, so both 0 and anything past 12
    // land on 12
    if (hourNum > 12 || hourNum === 0) {
      return '12';
    }
    return digits;
  }

  // bring a typed minute into range, under the same partial-entry rule as
  // clampHourText above
  private clampMinuteText(digits: string): string {
    if (digits.length !== 2) {
      return digits;
    }
    return parseInt(digits) > 59 ? '59' : digits;
  }

  handleHourInput(ev: Event) {
    const target = ev.target as HTMLInputElement;
    const value = target.value;
    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      target.value = this.hour;
      return;
    }

    // Limit to 2 digits
    if (value.length > 2) {
      target.value = this.hour;
      return;
    }

    this.hour = this.clampHourText(value);
    // if the entry was brought into range, correct what the box shows too
    if (this.hour !== value) {
      target.value = this.hour;
    }
  }

  handleMinuteInput(ev: Event) {
    const target = ev.target as HTMLInputElement;
    const value = target.value;
    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      target.value = this.minute;
      return;
    }

    // Limit to 2 digits
    if (value.length > 2) {
      target.value = this.minute;
      return;
    }

    this.minute = this.clampMinuteText(value);
    // if the entry was brought into range, correct what the box shows too
    if (this.minute !== value) {
      target.value = this.minute;
    }
  }

  async presentFormatPopover(event: Event) {
    event.stopPropagation();

    const containerEl = this.elementRef.nativeElement as HTMLElement;

    const result = await this.popupMenu.createMenu({
      title: LL.common.pickerTitles.timeFormat(),
      buttons: [
        { text: untranslatedString('24h'), value: TimeFormat.TwentyFourHour },
        { text: untranslatedString('AM'), value: TimeFormat.TwelveHourAM },
        { text: untranslatedString('PM'), value: TimeFormat.TwelveHourPM },
      ],
      value: this.currentFormat,
      targetFromElement: containerEl,
    });

    // Reset the flag
    const wasSelectingFromKeyboard = this.isSelectingFormatFromKeyboard;
    this.isSelectingFormatFromKeyboard = false;

    const value = result.value;
    if (value && value !== this.currentFormat) {
      this.applyFormatChange(value);
    }

    // Refocus and restart keyboard to show updated format
    if (wasSelectingFromKeyboard && this.currentInputControl) {
      // Restart the keyboard with updated options (format has changed)
      void this.keyboardService.startKeyboard(
        this.currentInputControl,
        () => this.getKeyboardOptions()
      );
    }
  }

  // switch the picker to another format, converting the hour box to match.
  // Shared by the popup menu and the on-screen keyboard's unit selector, so
  // the two routes give the same result (df-49288ba2b572).
  private applyFormatChange(newFormat: TimeFormat) {
    // the clock the hour is on now is read from is24h and isPM rather than
    // currentFormat: currentFormat is left unset when the bound time does
    // not parse, while is24h and isPM always hold a value
    const oldFormat = this.is24h
      ? TimeFormat.TwentyFourHour
      : (this.isPM ? TimeFormat.TwelveHourPM : TimeFormat.TwelveHourAM);
    const converted = convertHourForFormat(this.hour, oldFormat, newFormat);
    this.currentFormat = newFormat;
    this.is24h = newFormat === TimeFormat.TwentyFourHour;
    this.hour = converted.hourText;
    this.isPM = converted.isPM;

    this.emitValue();
    this.cdr.markForCheck();
  }

  emitValue() {
    if (!this.hour || !this.minute) {
      return;
    }

    const hourNum = this.is24h ? parseHourText(this.hour) : to24hHour(this.hour, this.isPM);
    // the boxes accept digits only, so an hour that does not parse is
    // treated like an empty one: nothing is emitted until there is an hour
    if (hourNum === undefined) {
      return;
    }

    const time = `${hourNum.toString().padStart(2, '0')}:${this.minute.padStart(2, '0')}`;
    this.timeChange.emit(time);
  }

  onInputFocused(isHour: boolean) {
    if (isHour) {
      this.hourEdit = '';
    } else {
      this.minuteEdit = '';
    }
  }

  onInputLostFocus(isHour: boolean) {
    // Pad with zero if single digit
    if (isHour && this.hour.length === 1) {
      this.hour = '0' + this.hour;
    } else if (!isHour && this.minute.length === 1) {
      this.minute = '0' + this.minute;
    }
    this.emitValue();
  }

  handleButtonClick(event: Event) {
    event.stopPropagation();
    if (this.hourInput) {
      this.hourInput.nativeElement.focus();
    }
  }

  get formatLabel(): string {
    if (this.is24h) {
      return '24h';
    } else {
      return this.isPM ? 'PM' : 'AM';
    }
  }

  isTouchDevice() {
    return ('ontouchstart' in document.documentElement);
  }

  useCustomKeyboard() {
    return this.isTouchDevice();
  }

  private generateHourValues(): InputValue[] {
    const values: InputValue[] = [];
    const max = this.is24h ? 23 : 12;
    const min = this.is24h ? 0 : 1;

    for (let i = min; i <= max; i++) {
      values.push(InputValue.fromNumber(i, 0, '.'));
    }

    return values;
  }

  private generateMinuteValues(): InputValue[] {
    const values: InputValue[] = [];

    for (let i = 0; i <= 59; i++) {
      values.push(InputValue.fromNumber(i, 0, '.'));
    }

    return values;
  }

  private getFormatUnitConfigs(): MeasureUnitConfig[] {
    return [
      {
        id: TimeFormat.TwentyFourHour,
        unit1: '24h',
        value1: 1,
        description: '24-hour format'
      },
      {
        id: TimeFormat.TwelveHourAM,
        unit1: 'AM',
        value1: 1,
        description: 'AM (morning)'
      },
      {
        id: TimeFormat.TwelveHourPM,
        unit1: 'PM',
        value1: 1,
        description: 'PM (afternoon/evening)'
      }
    ];
  }

  private getKeyboardOptions() {
    // The keyboard asks for these options again every time it re-initializes,
    // and two of those re-initializations happen just after it called our
    // onClose — which cleared currentInputControl — while it stayed open on
    // the same box. So put the box back before building the options, or
    // everything below that depends on knowing which box is being edited
    // silently does nothing (#4904).
    if (!this.currentInputControl && this.keyboardTargetInput) {
      this.currentInputControl = this.keyboardTargetInput;
    }

    const hourValues = this.generateHourValues();
    const minuteValues = this.generateMinuteValues();

    // Parse current hour and minute - these should already be in the correct format
    // after onKeyboardFormatChange converts them
    const currentHour = parseInt(this.hour) || (this.is24h ? 0 : 1);
    const currentMinute = parseInt(this.minute) || 0;

    const options: any = {
      isDualRoller: true,
      preselectValuesNumeric1: hourValues,
      preselectValuesNumeric2: minuteValues,
      currentValueNumeric1: currentHour,
      currentValueNumeric2: currentMinute,
      suggestedValueNumeric1: currentHour,
      suggestedValueNumeric2: currentMinute,
      startInRollerMode: true,
      // tell the keyboard which box it is editing, so that after a
      // re-initialization it reads the box's current value into its preview
      // instead of showing an empty one
      targetElement: this.currentInputControl,
      rollerLabel1: 'HOURS',
      rollerLabel2: 'MINUTES',
      unitConfigs: this.getFormatUnitConfigs(),
      currentUnitId: this.currentFormat,
      alwaysShowUnit: true,
      onValueSelectedNumeric: (values: { value1: number; value2?: number }) => {
        // Update hour and minute from roller
        this.hour = values.value1.toString().padStart(2, '0');
        this.minute = (values.value2 || 0).toString().padStart(2, '0');
        this.emitValue();
        this.cdr.markForCheck();
      },
      // The on-screen keyboard has two views and they report their result
      // through different callbacks: the wheels call onValueSelectedNumeric
      // above with both numbers, while the number pad calls onTextChange with
      // the text typed so far. Without this second one the number pad
      // delivered nothing at all, so Save kept the old time (#4904).
      onTextChange: (text: string) => this.applyTypedText(text),
      onUnitChange: async (unitId: string) => {
        await this.onKeyboardFormatChange(unitId);
      },
      onClose: () => {
        this.currentInputControl = undefined;
      }
    };

    return options;
  }

  // Take what the user has typed on the number pad and put it into whichever
  // of the two boxes the keyboard is attached to. The keyboard is opened on
  // one box at a time, so the box it was opened on is the one being typed
  // into. Called on every keystroke as well as on Accept, which is what makes
  // the box on screen follow the typing.
  // Returns the text actually kept, so the keyboard can show that in its own
  // preview rather than the raw typing — see adoptCorrectedText there.
  private applyTypedText(text: string): string {
    const isHour = !!this.hourInput && this.currentInputControl === this.hourInput.nativeElement;
    const isMinute = !!this.minuteInput && this.currentInputControl === this.minuteInput.nativeElement;
    // if the keyboard is on neither box, there is nothing this can safely do,
    // so leave the typed text as it is
    if (!isHour && !isMinute) {
      return text;
    }

    // the number pad can also produce a decimal point, and it does not stop
    // at two characters, so keep only the first two digits
    const digits = text.replace(/\D/g, '').slice(0, 2);

    if (isHour) {
      this.hour = this.clampHourText(digits);
    } else {
      this.minute = this.clampMinuteText(digits);
    }

    this.emitValue();
    this.cdr.markForCheck();

    return isHour ? this.hour : this.minute;
  }

  private async onKeyboardFormatChange(formatId: string) {
    const newFormat = formatId as TimeFormat;
    if (newFormat === this.currentFormat) {
      return;
    }

    this.applyFormatChange(newFormat);

    // IMPORTANT: The keyboard will call getFreshOptions() after this returns,
    // which will use the newly converted hour/minute values
  }

  onFocusIn(event: FocusEvent) {
    if (!this.useCustomKeyboard()) {
      return;
    }

    if (event && event.target && event.target instanceof HTMLInputElement) {
      this.startKeyboardForInput(event.target);
    }
  }

  private startKeyboardForInput(inputElement: HTMLInputElement) {
    if (!this.useCustomKeyboard() || !inputElement) return;

    this.currentInputControl = inputElement;
    this.keyboardTargetInput = inputElement;
    void this.keyboardService.startKeyboard(
      this.currentInputControl,
      () => this.getKeyboardOptions()
    );
  }

  async onInputBlur(event: Event) {
    void event;

    // Don't stop keyboard if we're selecting format
    if (this.isSelectingFormatFromKeyboard) {
      return;
    }

    // Stop keyboard if focus moved to a non-input element
    if (this.currentInputControl) {
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isInput = activeElement?.tagName === 'INPUT' ||
                       activeElement?.closest('cs-input-time');

        if (!isInput) {
          void this.keyboardService.stopKeyboard();
        }
      }, 0);
    }
  }
}